import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { doGetClasses } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import _ from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';

import DownloadButton from '../../DownloadButton';
import Pagination from '../../Pagination';
import config from '../../../lib/config';
import { warningVariant, errorVariant } from '../../../lib/barVariant';
import Loader from '../../Loader';
import { IClass } from '../../../models/campusClasses.model';
import exportTable from '../../../lib/exportTable';
import styled from 'styled-components';
import { getCommonCodeLabel } from '../../../lib/store';

const Block = styled.div`
  .closed {
    background: gainsboro;
  }
`;

interface IProps {
  refreshTable: boolean;
};

const ClassTable: FC<IProps> = ({
  refreshTable,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { isLoading } = useSelector((state: RootState) => state.class);

  const [data, setData] = useState<IClass[]>([]);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState({});
  const [classCode, setClassCode] = useState('');
  const [isFirstRun, setIsFirstRun] = useState(true);

  useEffect(() => {
    if (query.campus && query.semester) {
      fetchData();
    }
  }, [
    query.campus,
    query.semester,
    query.eduStepCode,
    query.keyword,
    query.includeClosed,
  ]);

  useEffect(() => {
    if (query.page) {
      setPage(+query.page);
    }
  }, [query.page]);

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    fetchData();
  }, [refreshTable]);

  const fetchData = async () => {
    // get data
    const res = await getClasses(query.campus, query.semester);

    // data filtering
    let _data: IClass[] = _.cloneDeep(res);
    const _params: any = {
      page: 1,
      campus: query.campus,
      semester: query.semester,
    };

    if (query.eduStepCode) {
      _params.eduStepCode = String(query.eduStepCode);
      _data = _data.filter((item: IClass) => item.eduStepCode === _params.eduStepCode);
    }
    if (query.keyword) {
      _params.keyword = String(query.keyword).toLowerCase();
      _data = _data.filter((item: IClass) => item.className?.toLowerCase().includes(_params.keyword));
    }
    if (query.includeClosed) {
      _params.includeClosed = true;
    } else {
      _data = _data.filter((item: IClass) => item.classStatus === 'OP');
    }
    if (query.page) {
      _params.page = Number(query.page);
      setPage(_params.page);
    }

    setParams(_params);
    setData(_data);
  }

  const getClasses = async (campusCode: any, semesterCode: any): Promise<IClass[]> => {
    const res = await dispatch(doGetClasses(campusCode, semesterCode));

    switch (res?.status) {
      case 200:
        return res?.data?.data;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }

    return [];
  }

  // 학급 선택 시 쿼리 반영
  const refreshQuery = (classCode: string) => {
    const param: any = { page, classCode };

    if (query.campus) {
      param.campus = query.campus;
    }
    if (query.semester) {
      param.semester = query.semester;
    }
    if (query.eduStepCode) {
      param.eduStepCode = query.eduStepCode;
    }
    if (query.keyword) {
      param.keyword = query.keyword;
    }
    if (query.includeClosed) {
      param.includeClosed = true;
    }

    history.push(`/class/class?${qs.stringify(param)}`);
  }

  const onExportTable = () => {
    const currentDate = moment(new Date());
    exportTable(columns, data, `${t('class.class.title1')}_(${currentDate.format('YYYYMMDD')})`, `${t('class.class.title1')}`, [15, 25]);
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('class.class.column1'),
      dataIndex: 'courseDetailName',
      key: 'courseDetailName',
      sorter: (a, b) => a.courseDetailName?.localeCompare(b.courseDetailName),
      align: 'center'
    },
    {
      title: t('class.class.column2'),
      dataIndex: 'className',
      key: 'className',
      sorter: (a, b) => a.className?.localeCompare(b.className),
      align: 'center',
      render: (value, record) => record.classStatus === 'CL'
        ? `${value} (${t('class.class.column9')})`
        : value
    }
  ];

  return (
    <Block>
      {isLoading ? <Loader /> : null}
      <div className="card">
        <div className="card-header">
          {t('common.text.count')}: {data?.length || 0}{t('common.text.unit')}
        </div>
        <div className="card-body table-wrapper">
          <Table
            columns={columns}
            dataSource={data || undefined}
            pagination={{
              current: page,
              pageSize: config.smallPageSize
            }}
            onRow={record => {
              return {
                onClick: () => {
                  setClassCode(record.classCode);
                  refreshQuery(record.classCode);
                }
              };
            }}
            rowClassName={(record: any) =>
              record.classCode === classCode
                ? 'chosen'
                : record.classStatus === 'CL'
                  ? 'closed'
                  : 'pointer'
            }
            rowKey="classCode"
          />
        </div>
        <div className="card-footer clearfix flex">
          <div className="w-75">
            <DownloadButton
              onClick={onExportTable}
            />
          </div>
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              param={params}
              path="/class/class"
              small
            />
          </div>
        </div>
      </div>
    </Block>
  );
}

export default ClassTable;
